import { useState } from "react";

import { RegistryTable } from "../components/RegistryTable";
import { Card, EmptyState, PageHeader, Tabs } from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";

const INTEGRATION_TYPES = [
  "rest_api",
  "graphql",
  "webhook",
  "edi",
  "sftp",
  "csv",
  "excel",
  "email",
  "manual_portal",
  "database",
  "other",
];

const AUTH_TYPES = ["oauth2", "api_key", "basic", "sftp_key", "mutual_tls", "session", "none", "unknown"];

export function IntegrationsPage() {
  const { organizationId } = useCurrentOrganization();
  const [tab, setTab] = useState<"integrations" | "edi">("integrations");

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Integration registry"
        description="Connection metadata only. This system never stores credentials — `auth_type` names the mechanism and nothing more."
      />

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "integrations", label: "Integrations" },
          { value: "edi", label: "EDI transactions" },
        ]}
      />

      {tab === "integrations" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="integrations"
          emptyTitle="No integrations recorded"
          emptyDescription="Integrations appear as interviews describe how data moves between systems and partners."
          columns={[
            { key: "name", label: "Integration" },
            { key: "integration_type_code", label: "Type" },
            { key: "direction", label: "Direction" },
            { key: "data_objects", label: "Data objects" },
            { key: "frequency", label: "Frequency", kind: "text" },
            { key: "auth_type", label: "Auth mechanism" },
            { key: "status", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Name", required: true },
            {
              key: "integration_type_code",
              label: "Type",
              required: true,
              options: INTEGRATION_TYPES,
            },
            {
              key: "direction",
              label: "Direction",
              options: ["inbound", "outbound", "bidirectional", "unknown"],
            },
            { key: "transport", label: "Transport" },
            { key: "frequency", label: "Frequency" },
            { key: "auth_type", label: "Auth mechanism (metadata only)", options: AUTH_TYPES },
            {
              key: "status",
              label: "Status",
              options: ["planned", "in_development", "test", "production", "deprecated", "unknown"],
            },
            { key: "failure_behavior", label: "Failure behaviour" },
            { key: "manual_fallback", label: "Manual fallback" },
          ]}
        />
      ) : (
        <RegistryTable
          organizationId={organizationId}
          resource="edi-transactions"
          emptyTitle="No EDI transactions recorded"
          emptyDescription="850, 855, 856, 810, 846, 940 and 945 are captured per trading partner."
          columns={[
            { key: "transaction_set", label: "Set" },
            { key: "partner_name", label: "Partner" },
            { key: "provider_van", label: "Provider / VAN", kind: "text" },
            { key: "transport", label: "Transport", kind: "text" },
            { key: "frequency", label: "Frequency", kind: "text" },
            { key: "production_status", label: "Production", kind: "status" },
            { key: "known_exceptions", label: "Known exceptions", kind: "text" },
          ]}
          createFields={[
            {
              key: "transaction_set",
              label: "Transaction set",
              required: true,
              options: ["850", "855", "856", "810", "846", "940", "945", "997", "other"],
            },
            { key: "partner_name", label: "Trading partner", required: true },
            { key: "sender_id", label: "Sender ID" },
            { key: "receiver_id", label: "Receiver ID" },
            { key: "provider_van", label: "Provider / VAN" },
            { key: "transport", label: "Transport" },
            { key: "frequency", label: "Frequency" },
            { key: "test_status", label: "Test status" },
            { key: "production_status", label: "Production status" },
            { key: "known_exceptions", label: "Known exceptions" },
          ]}
        />
      )}
    </>
  );
}
